import { useEffect, useState } from "react";
import { getCandidate, loadCandidateVacancyTitle } from "../../services/candidateApi";
import { createEvent, searchEventEmployees } from "../../services/eventsApi";
import { useAlertContext } from "../../context/AlertContext";
import { formatTelegramUsername, relationVacancyName } from "../../utils/candidateMapper";
import {
    buildInterviewReminderFormState,
    buildInterviewReminderNote,
    interviewTimeRange,
} from "../../utils/interviewReminder";
import { normalizeRemindBefore } from "../events/remindBefore";
import "./CalendarDialogs.css";

export default function InterviewReminderModal({
    open,
    candidate,
    date,
    startTime,
    endTime,
    onClose,
    onSkip,
    onSaved,
}) {
    const { showAlert } = useAlertContext();
    const [form, setForm] = useState(null);
    const [loadingForm, setLoadingForm] = useState(false);
    const [saving, setSaving] = useState(false);
    const [hrQuery, setHrQuery] = useState("");
    const [hrSuggestions, setHrSuggestions] = useState([]);
    const [hrSearching, setHrSearching] = useState(false);
    const [hrPickedFromErp, setHrPickedFromErp] = useState(false);

    useEffect(() => {
        if (!open || !candidate?.id) {
            setForm(null);
            setLoadingForm(false);
            setHrQuery("");
            setHrSuggestions([]);
            setHrPickedFromErp(false);
            return undefined;
        }
        let cancelled = false;
        const snapshot = candidate;
        setLoadingForm(true);
        (async () => {
            try {
                let full = { ...snapshot };
                try {
                    const fresh = await getCandidate(snapshot.id);
                    if (fresh && typeof fresh === "object") full = { ...full, ...fresh };
                } catch (_) {
                    /* keep payload from the invite */
                }
                let vacancyName =
                    relationVacancyName(full)
                    || full.vacancy_name
                    || full.last_vacancy_title
                    || "";
                try {
                    vacancyName = (await loadCandidateVacancyTitle(snapshot.id)) || vacancyName;
                } catch (_) {
                    /* keep title from candidate */
                }
                if (cancelled) return;
                setForm(
                    buildInterviewReminderFormState({
                        candidateName: full.full_name || `Кандидат #${snapshot.id}`,
                        vacancyName,
                        eventDate: date,
                        interviewTime: interviewTimeRange(startTime, endTime),
                        remindAtTime: String(startTime || "").slice(0, 5),
                        candidatePhone: full.phone_number || full.phone || "",
                        candidateEmail: full.email || "",
                        candidateTelegram: formatTelegramUsername(full.telegram_username) || "",
                    })
                );
                setHrQuery("");
                setHrSuggestions([]);
                setHrPickedFromErp(false);
            } finally {
                if (!cancelled) setLoadingForm(false);
            }
        })();
        return () => {
            cancelled = true;
        };
        // Prefill from the candidate snapshot passed by the invite/booking flow.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, candidate?.id, date, startTime, endTime]);

    useEffect(() => {
        if (!open || loadingForm) return undefined;
        const q = String(hrQuery || "").trim();
        if (q.length < 2) {
            setHrSuggestions([]);
            setHrSearching(false);
            return undefined;
        }
        let cancelled = false;
        setHrSearching(true);
        const timer = setTimeout(async () => {
            try {
                const rows = await searchEventEmployees(q, { by: "name", limit: 15 });
                if (!cancelled) setHrSuggestions(Array.isArray(rows) ? rows : []);
            } catch {
                if (!cancelled) setHrSuggestions([]);
            } finally {
                if (!cancelled) setHrSearching(false);
            }
        }, 250);
        return () => {
            cancelled = true;
            clearTimeout(timer);
        };
    }, [hrQuery, open, loadingForm]);

    const updateField = (patch) => {
        setForm((prev) => {
            if (!prev) return prev;
            const next = { ...prev, ...patch };
            next.note = buildInterviewReminderNote({
                candidateName: next.candidate_name,
                vacancyName: next.vacancy_name,
                eventDate: next.event_date,
                interviewTime: next.interview_time,
                candidatePhone: next.candidate_phone,
                candidateEmail: next.candidate_email,
                candidateTelegram: next.candidate_telegram,
            });
            return next;
        });
    };

    const handleSave = async () => {
        if (!form) return;
        if (!String(form.candidate_name || "").trim()) {
            showAlert("Укажите кандидата для события собеседования", "error");
            return;
        }
        if (!form.event_date) {
            showAlert("Укажите дату собеседования", "error");
            return;
        }
        if (!String(form.interview_time || "").trim()) {
            showAlert("Укажите время собеседования", "error");
            return;
        }
        if (!hrPickedFromErp || !String(form.telegram_user || "").trim()) {
            showAlert("Выберите Telegram сотрудника из ERP-подсказок", "error");
            return;
        }
        try {
            setSaving(true);
            await createEvent({
                type: "interview",
                employee_name: null,
                event_date: form.event_date,
                remind_before: normalizeRemindBefore(form.remind_before, 1),
                remind_at_time: String(form.remind_at_time || "").trim() || null,
                telegram_user: String(form.telegram_user || "").trim() || null,
                note: String(form.note || "").trim() || null,
            });
            showAlert("Напоминание о собеседовании сохранено", "success");
            if (onSaved) onSaved();
            else onClose?.();
        } catch (e) {
            showAlert(`Ошибка сохранения напоминания: ${e.message || e}`, "error");
        } finally {
            setSaving(false);
        }
    };

    if (!open) return null;

    return (
        <div className="calendar-dialog-backdrop !z-[120]">
            <div className="calendar-dialog w-full !max-w-2xl max-h-[calc(100vh-2rem)] flex flex-col" role="dialog" aria-modal="true" aria-labelledby="reminder-dialog-title">
                <div className="calendar-dialog-header shrink-0">
                    <div>
                        <span className="calendar-dialog-kicker">ПОСЛЕ БРОНИРОВАНИЯ</span>
                        <h3 id="reminder-dialog-title">Напоминание сотруднику</h3>
                        <p>Выберите коллегу из ERP — он получит в Telegram все детали собеседования.</p>
                    </div>
                    <button type="button" className="calendar-dialog-close" onClick={onClose} disabled={saving} aria-label="Закрыть">×</button>
                </div>
                <div className="calendar-dialog-body min-h-0 overflow-y-auto">
                {loadingForm || !form ? (
                    <div className="py-12 text-center"><div className="inline-block w-6 h-6 rounded-full border-2 border-slate-200 border-t-[#9c7621] animate-spin"/><p className="text-sm text-slate-500 mt-3">Подтягиваем данные кандидата…</p></div>
                ) : (
                    <div className="space-y-5" data-testid="interview-reminder-form">
                        <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
                            <div className="flex items-center justify-between gap-3 mb-3"><span className="text-[11px] font-bold tracking-wider text-slate-500 uppercase">Детали встречи</span><span className="px-2.5 py-1 rounded-full bg-white border border-slate-200 text-xs font-semibold text-slate-600">Собеседование</span></div>
                            <div className="grid sm:grid-cols-2 gap-3">
                                <div><span className="block text-xs text-slate-500">Дата</span><strong className="text-sm text-slate-900">{form.event_date || "—"}</strong></div>
                                <div><span className="block text-xs text-slate-500">Время</span><strong className="text-sm text-slate-900">{form.interview_time || "—"}</strong></div>
                            </div>
                        </div>
                        <div className="grid sm:grid-cols-2 gap-4">
                        <label className="block text-sm">
                            <span className="calendar-field-label">Кандидат</span>
                            <input
                                className="calendar-field"
                                value={form.candidate_name}
                                onChange={(e) => updateField({ candidate_name: e.target.value })}
                            />
                        </label>
                        <label className="block text-sm">
                            <span className="calendar-field-label">Вакансия</span>
                            <input
                                className="calendar-field"
                                value={form.vacancy_name}
                                onChange={(e) => updateField({ vacancy_name: e.target.value })}
                            />
                        </label>
                        </div>
                        <div className="grid sm:grid-cols-2 gap-4">
                            <label className="block text-sm">
                                <span className="calendar-field-label">Дата собеседования</span>
                                <input
                                    type="date"
                                    className="calendar-field"
                                    value={form.event_date}
                                    onChange={(e) => updateField({ event_date: e.target.value })}
                                />
                            </label>
                            <label className="block text-sm">
                                <span className="calendar-field-label">Время собеседования</span>
                                <input
                                    className="calendar-field"
                                    value={form.interview_time}
                                    onChange={(e) => updateField({ interview_time: e.target.value })}
                                />
                            </label>
                        </div>
                        <div className="grid sm:grid-cols-2 gap-4">
                            <label className="block text-sm">
                                <span className="calendar-field-label">Напомнить за, дней</span>
                                <input
                                    type="number"
                                    min="0"
                                    className="calendar-field"
                                    value={form.remind_before}
                                    onChange={(e) => setForm((prev) => ({ ...prev, remind_before: e.target.value }))}
                                />
                            </label>
                            <label className="block text-sm">
                                <span className="calendar-field-label">Время напоминания</span>
                                <input
                                    type="time"
                                    className="calendar-field"
                                    value={form.remind_at_time}
                                    onChange={(e) => setForm((prev) => ({ ...prev, remind_at_time: e.target.value }))}
                                />
                            </label>
                        </div>
                        <label className="block text-sm">
                            <span className="calendar-field-label">Кому напомнить в Telegram <b>*</b></span>
                            <input
                                className="calendar-field"
                                value={hrQuery}
                                onChange={(e) => {
                                    setHrQuery(e.target.value);
                                    setHrPickedFromErp(false);
                                }}
                                placeholder="Начните вводить ФИО сотрудника"
                            />
                            {hrSearching && <p className="text-xs text-slate-400 mt-2">Ищем сотрудника в ERP…</p>}
                            {!hrSearching && hrSuggestions.length > 0 && (
                                <div className="mt-2 border border-slate-200 rounded-xl max-h-48 overflow-auto shadow-lg shadow-slate-200/50">
                                    {hrSuggestions.map((item) => (
                                        <button
                                            key={item.erp_user_id}
                                            type="button"
                                            className="block w-full text-left px-4 py-3 text-sm hover:bg-[#fbf8f0] border-b border-slate-100 last:border-b-0"
                                            onClick={() => {
                                                setHrQuery(item.full_name || "");
                                                setForm((prev) => ({
                                                    ...prev,
                                                    telegram_user: item.tg_username || "",
                                                }));
                                                setHrSuggestions([]);
                                                setHrPickedFromErp(true);
                                            }}
                                        >
                                            <div className="font-medium text-slate-900">{item.full_name || "—"}</div>
                                            <div className="text-xs text-slate-500">{item.tg_username || "без telegram"}</div>
                                        </button>
                                    ))}
                                </div>
                            )}
                            {form.telegram_user && (
                                <p className="text-xs text-emerald-700 mt-2 rounded-lg bg-emerald-50 border border-emerald-100 px-3 py-2">
                                    Telegram выбран: <span className="font-semibold">{form.telegram_user}</span>
                                </p>
                            )}
                        </label>
                        <label className="block text-sm">
                            <span className="calendar-field-label">Заметка для напоминания</span>
                            <textarea
                                rows={4}
                                className="calendar-field !h-auto py-3 resize-y"
                                value={form.note}
                                onChange={(e) => setForm((prev) => ({ ...prev, note: e.target.value }))}
                            />
                        </label>
                    </div>
                )}
                </div>
                <div className="calendar-dialog-footer shrink-0 flex-wrap">
                    <button
                        type="button"
                        onClick={onClose}
                        className="calendar-button calendar-button--secondary"
                        disabled={saving}
                    >
                        Отмена
                    </button>
                    <button
                        type="button"
                        onClick={() => (onSkip || onClose)?.()}
                        disabled={saving}
                        className="calendar-button calendar-button--secondary"
                    >
                        Пропустить
                    </button>
                    <button
                        type="button"
                        onClick={handleSave}
                        disabled={saving || loadingForm || !form}
                        className="calendar-button calendar-button--primary"
                    >
                        {saving ? "Сохранение..." : "Сохранить напоминание"}
                    </button>
                </div>
            </div>
        </div>
    );
}
